import { Component } from 'preact';
import { connect } from 'unistore/preact';
import { Text } from 'preact-i18n';
import get from 'get-value';

import { ConfigField } from '../../../../components/integration/ConfigSchemaForm';
import { hasDynamicSource, fetchDynamicOptions } from '../../../../components/integration/dynamicOptions';
import ExternalIntegrationDeclarationState from '../ExternalIntegrationDeclarationState';
import { getLocalizedText } from '../../../../utils/getLocalizedText';
import {
  SCENE_DECLARATION_KINDS,
  resolveSceneDeclaration,
  normalizeDeclaredFieldValue,
  buildDeclaredVariables
} from '../sceneIntegrations';

// A scene trigger declared by an external integration (scene_triggers of its
// manifest): the declared fields are the filters the user fills in, rendered
// by the config_schema form engine — a field left empty matches any value —
// and the declared variables are what the actions can read from the event
// ({{triggerEvent.data.<key>}}): they are declared to the editor for the
// variable picker of the text fields, and only named to the user, by label.
class ExternalIntegrationTrigger extends Component {
  getLanguage = () => get(this.props, 'user.language') || 'en';

  resolve = (props = this.props) =>
    resolveSceneDeclaration(props.sceneIntegrations, SCENE_DECLARATION_KINDS.trigger, props.trigger);

  loadDynamicOptions = async declaration => {
    // a select/multi_select filter can take its options from a core-defined
    // source: the devices of the integration (value = external_id) or the
    // houses of Gladys (value = selector)
    if (!declaration || !hasDynamicSource(declaration.fields)) {
      return;
    }
    // the response is bound to the declaration it was requested for: a
    // slower answer of a previous integration must not overwrite the current one
    const { integration, trigger_key: key } = this.props.trigger;
    try {
      const dynamicOptions = await fetchDynamicOptions(this.props.httpClient, integration, declaration.fields);
      if (this.props.trigger.integration !== integration || this.props.trigger.trigger_key !== key) {
        return;
      }
      this.setState({ dynamicOptions });
    } catch (e) {
      console.error(e);
    }
  };

  declareVariables = declaration => {
    // the picker inserts {{triggerEvent.data.<key>}}: the name is prefixed
    // with `data.`, the editor adds `triggerEvent.`
    this.props.setVariablesTrigger(
      this.props.index,
      buildDeclaredVariables(declaration ? declaration.variables : [], this.getLanguage(), 'data.')
    );
  };

  refresh = () => {
    const { declaration } = this.resolve();
    this.loadDynamicOptions(declaration);
    this.declareVariables(declaration);
  };

  updateFieldValue = (field, value) => {
    const fields = Object.assign({}, this.props.trigger.fields || {}, {
      [field.key]: normalizeDeclaredFieldValue(field, value)
    });
    this.props.updateTriggerProperty(this.props.index, 'fields', fields);
  };

  constructor(props) {
    super(props);
    this.state = { dynamicOptions: {} };
  }

  componentDidMount() {
    this.refresh();
  }

  componentDidUpdate(prevProps) {
    const declarationChanged =
      prevProps.trigger.integration !== this.props.trigger.integration ||
      prevProps.trigger.trigger_key !== this.props.trigger.trigger_key ||
      prevProps.sceneIntegrations !== this.props.sceneIntegrations;
    if (declarationChanged) {
      this.refresh();
    }
  }

  render(props, { dynamicOptions }) {
    if (props.sceneIntegrations === null) {
      // catalog unknown (pending or failed): neither live nor orphan yet
      return (
        <p class="text-muted small mb-0">
          <i class="fe fe-loader mr-1" />
          <Text id="editScene.externalIntegration.catalogLoading" />
        </p>
      );
    }
    const language = this.getLanguage();
    const { integration, declaration, inactive } = this.resolve(props);
    if (!integration || !declaration) {
      return (
        <ExternalIntegrationDeclarationState integration={integration} fields={props.trigger.fields} kind="trigger" />
      );
    }
    const description = getLocalizedText(declaration.description, language);
    const fields = declaration.fields || [];
    const variables = declaration.variables || [];
    return (
      <div>
        {inactive && (
          <div class="alert alert-warning">
            <i class="fe fe-pause-circle mr-1" />
            <Text id="editScene.externalIntegration.triggerInactive" />
          </div>
        )}
        {description && <p>{description}</p>}
        {fields.length > 0 && (
          <div>
            <p class="text-muted small">
              <Text id="editScene.externalIntegration.wildcardHint" />
            </p>
            {fields.map(field => (
              <ConfigField
                key={field.key}
                field={field}
                language={language}
                values={props.trigger.fields || {}}
                configuredSecrets={[]}
                touchedSecrets={{}}
                updateConfigValue={this.updateFieldValue}
                dynamicOptions={dynamicOptions}
                placeholderPorts={{}}
              />
            ))}
          </div>
        )}
        {variables.length > 0 && (
          <p class="text-muted small mb-0 mt-3">
            <Text id="editScene.externalIntegration.variablesAvailable" />{' '}
            {variables.map(variable => (
              <span key={variable.key} class="badge badge-secondary mr-1">
                {getLocalizedText(variable.label, language) || variable.key}
              </span>
            ))}
          </p>
        )}
      </div>
    );
  }
}

export default connect('httpClient,user', {})(ExternalIntegrationTrigger);
